import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam, AnyType } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";
import { toLocaleString } from "./common";

type InternalProjectRow = AnyType;

export interface IGetProjectsResponceModel {
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * プロジェクト名
   */
  name: string;
  /**
   * メモ
   */
  memo: string;
  /**
   * ファイル数
   */
  filenum: number;
  /**
   * 更新日時
   */
  updatetime: string;
  /**
   * 診断モデル作成状態
   */
  mcreatests: string;
  /**
   * 診断モデル作成日時
   */
  mcreatetime: string;
  /**
   * 診断モデルテスト状態
   */
  mteststs: string;
  /**
   * 診断モデルテスト日時
   */
  mtesttime: string;
  /**
   * プロファイル作成状態
   */
  pcreatests: string;
  /**
   * プロファイル作成日時
   */
  pcreatetime: string;
  /**
   * ETAG
   */
  etag: string;
}

export interface IProjectRow extends IGetProjectsResponceModel {
  id: number;
}

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIからプロジェクト一覧の取得が成功した場合に呼び出されます
   * @callback success
   * @param {IProjectRow[]} rows - プロジェクト一覧
   * @param {number} status - httpステータス
   */
  success: (rows: IProjectRow[], status: number) => void;
}

function createRow(index: number, row: InternalProjectRow): IProjectRow {
  row.id = index;
  row.updatetime = toLocaleString(new Date(Date.parse(row.updatetime)));
  row.mcreatetime = row.mcreatetime
    ? toLocaleString(new Date(Date.parse(row.mcreatetime)))
    : "";
  row.pcreatetime = row.pcreatetime
    ? toLocaleString(new Date(Date.parse(row.pcreatetime)))
    : "";
  row.etag = row.etag ? row.etag : "";
  return row;
}

function createRows(data: IGetProjectsResponceModel[]): IProjectRow[] {
  if (Array.isArray(data)) {
    const rows: InternalProjectRow[] = data.slice();
    rows.forEach((row, i) => {
      row = createRow(i, row);
    });
    return rows;
  } else {
    return [createRow(0, data)];
  }
}

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: { Authorization: "Bearer " + token },
    });
  } else {
    return axios.get(url);
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      params.success(createRows(res.data), res.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface IGetProjectsParams extends ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectid?: string;
}

/**
 * プロジェクト一覧を取得します
 * @function getProjects
 * @param {IGetProjectsParams} params - プロジェクト一覧取得パラメータ
 */
export function getProjects(params: IGetProjectsParams): void {
  const { authService, projectid } = params;
  let url = WebAPI.GET_PROJECTS;
  if (projectid) {
    url = url + "/" + projectid;
  }
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosGet(url, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              getProjectsRetry401(authService, url, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosGet(url),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * プロジェクト一覧を取得します
 * @function getProjectsRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function getProjectsRetry401(
  authService: AuthService,
  url: string,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosGet(url, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}
